<ng-template #actions>
  @if (editable() || loading) {
    <div class="actions">
      <rt-group>
        @if (!form.enabled && !loading) {
          <rt-button variant="tertiary" (click)="edit.emit()">
            {{ 'app.common.edit' | translate }}
          </rt-button>
        }
        @if (form.enabled || loading) {
          <rt-button
            variant="primary"
            [loading]="loading"
            [disabled]="form.invalid || form.pristine || loading"
            (click)="setForm(stripValue(form.value)); save.emit(form.value)"
          >
            {{ 'app.common.save' | translate }}
          </rt-button>
        }
        @if (form.enabled || loading) {
          <rt-button variant="tertiary" [disabled]="loading" (click)="discard.emit()">
            {{ 'app.common.cancel' | translate }}
          </rt-button>
        }
      </rt-group>
    </div>
  }
</ng-template>

<rt-box
  variant="vertical no-padding"
  [class.no-background]="boxStyle() === 'transparent'"
  [class.no-border]="boxStyle() === 'transparent'"
>
  @if (actionsVisible || showHeading) {
    <div class="box-header header">
      @if (showHeading) {
        <rt-heading class="subheading">
          {{ 'app.common.information' | translate }}
        </rt-heading>
      }

      @if (actionsVisible) {
        <ng-container *ngTemplateOutlet="actions" />
      }
    </div>
  }

  @if (showHeading) {
    <rt-divider />
  }

  <div class="box-body body" [class.loading]="loading">
    <ng-template #textPreviewTpl let-value>
      <p [style.whiteSpace]="'pre-wrap'">{{ value }}</p>
    </ng-template>

    <ng-template #emailPreviewTpl let-value>
      <div class="email-tpl" layout layout-align-items-center>
        <p (click)="switchToEmailTab.emit()" [style.whiteSpace]="'pre-wrap'">{{ value }}</p>
        <rt-icon-button
          (click)="copyToClipboard(value)"
          variant="plain"
          margin-left-small-x
          [rtTooltip]="'app.common.copy_to_clipboard' | translate"
          iconName="content-copy"
          size="small"
          shape="square"
        />
      </div>
    </ng-template>

    <ng-template #phonePreviewTpl let-value>
      <div class="phone-tpl" layout layout-align-items-center>
        <a href="tel:{{ value }}">
          <p [style.whiteSpace]="'pre-wrap'">{{ value }}</p>
        </a>
        <rt-icon-button
          (click)="copyToClipboard(value)"
          variant="plain"
          margin-left-small-x
          [rtTooltip]="'app.common.copy_to_clipboard' | translate"
          iconName="content-copy"
          size="small"
          shape="square"
        />
      </div>
    </ng-template>

    <!-- Emails -->
    @if (visibleFields().includes('emails')) {
      <rt-candidate-profile-information-editor-group
        [parent]="$any(form.get(['emails']))"
        [editing]="form.enabled"
        [previewTpl]="emailPreviewTpl"
        [label]="texts.labels.emails"
        [showAddButton]="canShowAddButton('emails')"
        [addLabel]="texts.buttons.add"
        [focusedControl]="focusedControl!"
        [serverError]="this.validationErrors().emails"
        [icon]="icons.emails"
        [rtProtectedControl]="form.get(['emails'])"
        rtProtectedControlName="{{ 'app.common.email' | translate }}"
        (addControl)="addControl(['emails'], true, true)"
        (removeControl)="removeControl(['emails', $event])"
        (reorderControl)="reorderControl(['emails'])"
      />
    }

    <!-- Phones -->
    @if (visibleFields().includes('phones')) {
      <rt-divider class="light" />
      <rt-candidate-profile-information-editor-group
        [parent]="$any(form.get(['phones']))"
        [editing]="form.enabled"
        [previewTpl]="phonePreviewTpl"
        [label]="texts.labels.phones"
        [showAddButton]="canShowAddButton('phones')"
        [addLabel]="texts.buttons.add"
        [focusedControl]="focusedControl!"
        [icon]="icons.phones"
        [editPhoneNumber]="true"
        [countryCode]="countryCode"
        [rtProtectedControl]="form.get(['phones'])"
        rtProtectedControlName="{{ 'app.candidate.phone_short' | translate }}"
        (addControl)="addControl(['phones'], true, true)"
        (removeControl)="removeControl(['phones', $event])"
        (reorderControl)="reorderControl(['phones'])"
      />
    }

    <!-- SocialLinks -->
    @if (
      (getGroup('socialLinks').length > 0 || form.enabled) &&
      visibleFields().includes('socialLinks')
    ) {
      <rt-divider class="light" />
      <ng-template #socialLinkPreviewTpl let-value>
        <a [href]="value" target="_blank">
          <img
            [src]="$safeNavigationMigration((value | rtSocialMedia | async)?.icon)"
            [style.vertical-align]="'top'"
          />
        </a>
      </ng-template>
      <rt-candidate-profile-information-editor-group
        [class.social-media-icons]="!form.enabled"
        [parent]="$any(form.get(['socialLinks']))"
        [editing]="form.enabled"
        [previewTpl]="socialLinkPreviewTpl"
        [label]="texts.labels.socialLinks"
        [showAddButton]="canShowAddButton('socialLinks')"
        [addLabel]="texts.buttons.add"
        [focusedControl]="focusedControl!"
        [placeholder]="texts.placeholders['socialLinks']"
        [icon]="icons.socialLinks"
        [rtProtectedControl]="form.get(['socialLinks'])"
        rtProtectedControlName="{{ 'app.common.socials' | translate }}"
        (blur)="checkUrl($event)"
        (addControl)="addControl(['socialLinks'], true, true)"
        (reorderControl)="reorderControl(['socialLinks'])"
        (removeControl)="removeControl(['socialLinks', $event])"
      />
    }

    <!-- Links -->
    @if ((getGroup('links').length > 0 || form.enabled) && visibleFields().includes('links')) {
      <rt-divider class="light" />
      <ng-template #linkPreviewTpl let-value>
        <a [href]="value" target="_blank">{{ value }}</a>
      </ng-template>
      <rt-candidate-profile-information-editor-group
        [parent]="$any(form.get(['links']))"
        [editing]="form.enabled"
        [previewTpl]="linkPreviewTpl"
        [label]="texts.labels.links"
        [showAddButton]="canShowAddButton('links')"
        [addLabel]="texts.buttons.add"
        [focusedControl]="focusedControl!"
        [placeholder]="texts.placeholders['links']"
        [icon]="icons.links"
        [rtProtectedControl]="form.get(['links'])"
        rtProtectedControlName="{{ 'app.common.links' | translate }}"
        (blur)="checkUrl($event)"
        (addControl)="addControl(['links'], true, true)"
        (removeControl)="removeControl(['links', $event])"
        (reorderControl)="reorderControl(['links'])"
      />
    }
    @if (form.enabled) {
      <rt-divider class="light" />
    }
  </div>

  @if (editable() && form.enabled && actionsVisible) {
    <rt-divider />
    <div class="box-footer footer">
      <ng-container *ngTemplateOutlet="actions" />
    </div>
  }
</rt-box>
